import React, { useState, useEffect } from "react";
import config from "../config/config";

const SEOHead = ({ pageType = "homepage" }) => {
  const [seoSettings, setSeoSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSEOSettings();
  }, [pageType]);

  const fetchSEOSettings = async () => {
    try {
      const response = await fetch(
        config.getApiUrl(`get_seo_settings.php?page_type=${pageType}`)
      );
      const data = await response.json();

      if (data.success && data.settings) {
        setSeoSettings(data.settings);
      }
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !seoSettings) {
    return null;
  }

  // Update document title
  if (seoSettings.page_title) {
    document.title = seoSettings.page_title;
  }

  // Update meta tags
  const updateMetaTag = (name, content) => {
    if (!content) return;

    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  const updatePropertyTag = (property, content) => {
    if (!content) return;

    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", property);
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Update basic SEO meta tags
  updateMetaTag("description", seoSettings.meta_description);
  updateMetaTag("keywords", seoSettings.meta_keywords);
  updateMetaTag("robots", seoSettings.robots);

  // Update Open Graph tags
  updatePropertyTag("og:title", seoSettings.og_title || seoSettings.page_title);
  updatePropertyTag(
    "og:description",
    seoSettings.og_description || seoSettings.meta_description
  );
  updatePropertyTag("og:image", seoSettings.og_image);
  updatePropertyTag("og:type", "website");
  updatePropertyTag(
    "og:url",
    seoSettings.canonical_url || window.location.href
  );

  // Update Twitter Card tags
  updatePropertyTag("twitter:card", "summary_large_image");
  updatePropertyTag(
    "twitter:title",
    seoSettings.twitter_title || seoSettings.page_title
  );
  updatePropertyTag(
    "twitter:description",
    seoSettings.twitter_description || seoSettings.meta_description
  );
  updatePropertyTag(
    "twitter:image",
    seoSettings.twitter_image || seoSettings.og_image
  );

  // Update canonical URL
  if (seoSettings.canonical_url) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = seoSettings.canonical_url;
  }

  // Add structured data if provided
  if (seoSettings.structured_data) {
    try {
      // Remove existing structured data
      const existingScripts = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      existingScripts.forEach((script) => {
        if (script.textContent.includes("seo-structured-data")) {
          script.remove();
        }
      });

      // Add new structured data
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = seoSettings.structured_data;
      script.setAttribute("data-seo", "structured-data");
      document.head.appendChild(script);
    } catch (error) {
      console.error("Error adding structured data:", error);
    }
  }

  return null; // This component doesn't render anything visible
};

export default SEOHead;
